import { AnnotationCheckIcon } from "Icons/AnnotationCheck";
import { ClockIcon } from "Icons/Clock";
import { PhoneCall01Icon } from "Icons/PhoneCall01";
import { Recording01Icon } from "Icons/Recording01";
import { VolumeMinIcon } from "Icons/VolumeMin";

import { useState } from "react";

import { css } from "@emotion/react";

import { ButtonLink } from "components/shared/library/ButtonLink";
import { Typography } from "components/shared/library/Typography";
import { WhiteLabelButton } from "components/shared/library/WhiteLabelButton";

import { useInterview } from "contexts/InterviewContext";
import { useWhiteLabel } from "contexts/WhiteLabelContext";

import { getInterviewPhoneNumber } from "apis/recruit";

import { getColorVisibleAgainstWhite } from "utils/colors";
import { down } from "utils/mediaQueryStrings";
import { formatPhoneNumber } from "utils/text";

import { purple, uiGray } from "constants/colors";

import { CandidateFlowCardHeader } from "./CandidateFlowCardHeader";

const style = {
  section: css({
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 16,
  }),
  callInfo: css({
    display: "flex",
    flexDirection: "row-reverse",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 8,
    width: "-webkit-fill-available",
    [down("mobile")]: { flexDirection: "column" },
  }),
  centerAlign: css({ textAlign: "center" }),
  grid: css({
    display: "grid",
    gridTemplateAreas: `'a a' 'b c' 'd d'`,
    width: "-webkit-fill-available",
    gap: 16,
    [down("mobile")]: { gridTemplateAreas: `'a' 'b' 'c' 'd'` },
    maxHeight: 400,
    overflow: "auto",
  }),
  box: css({
    borderRadius: 16,
    border: `1px solid ${uiGray[10]}`,
    padding: 16,
    display: "flex",
    flexDirection: "column",
    gap: 8,
  }),
  a: css({ gridArea: "a" }),
  b: css({ gridArea: "b" }),
  c: css({ gridArea: "c" }),
  d: css({ gridArea: "d" }),
  phoneNumber: css({ width: "100%" }),
  button: { button: css({ height: "unset" }) },
};
export const CallInfo = ({
  onClick,
  isDemo,
}: {
  onClick: () => void;
  isDemo?: boolean;
}) => {
  const [phoneNumber, setPhoneNumber] = useState<string>();
  const { isVideoEnabled, interviewDetails, interviewFlowId } = useInterview();
  const { interviewStyle } = useWhiteLabel(true);

  const isRecruitmentInterview =
    interviewDetails?.interviewType === "recruitment";

  return (
    <>
      <CandidateFlowCardHeader
        title="Start your interview"
        Icon={PhoneCall01Icon}
        subtitle={
          isVideoEnabled ? (
            "This interview requires you to be on video."
          ) : (
            <span>
              This interview is{" "}
              <Typography size="XS" color={uiGray[70]} weight="bold">
                voice-only
              </Typography>
              .
            </span>
          )
        }
      />
      <div css={style.grid}>
        <div css={[style.box, style.a]}>
          <ClockIcon
            stroke={
              interviewStyle?.accentColor
                ? getColorVisibleAgainstWhite(interviewStyle?.accentColor)
                : purple[600]
            }
          />
          <div>
            <Typography tag="p" weight="bold">
              {!isRecruitmentInterview
                ? "This won't take long"
                : "Takes about 15 minutes"}
            </Typography>
            <Typography size="XS" color={uiGray[70]}>
              {!isRecruitmentInterview
                ? "Your AI interviewer will lead you through the process."
                : "We want to get to know you and see if there's a match!"}
            </Typography>
          </div>
        </div>
        <div css={[style.box, style.b]}>
          <Recording01Icon
            stroke={
              interviewStyle?.accentColor
                ? getColorVisibleAgainstWhite(interviewStyle?.accentColor)
                : purple[600]
            }
          />
          <div>
            <Typography tag="p" weight="bold">
              {!isRecruitmentInterview
                ? "Be natural"
                : "Be natural and confident"}
            </Typography>
            <Typography size="XS" color={uiGray[70]}>
              {!isRecruitmentInterview
                ? "Speak as you normally would, the AI interviewer will understand."
                : "Show your qualities that aren’t on your resume or cover letter."}
            </Typography>
          </div>
        </div>
        <div css={[style.box, style.c]}>
          <VolumeMinIcon
            stroke={
              interviewStyle?.accentColor
                ? getColorVisibleAgainstWhite(interviewStyle?.accentColor)
                : purple[600]
            }
          />
          <div>
            <Typography tag="p" weight="bold">
              Quiet spot and strong wifi
            </Typography>
            <Typography size="XS" color={uiGray[70]}>
              Make sure your audio is crisp and clear upon review.
            </Typography>
          </div>
        </div>
        <div css={[style.box, style.d]}>
          <AnnotationCheckIcon
            stroke={
              interviewStyle?.accentColor
                ? getColorVisibleAgainstWhite(interviewStyle?.accentColor)
                : purple[600]
            }
          />
          <div>
            <Typography tag="p" weight="bold">
              Reviewed by a human
            </Typography>
            <Typography size="XS" color={uiGray[70]}>
              {!isRecruitmentInterview
                ? "Each call is reviewed by someone on our team."
                : "Each call is seen by a recruiter so you’re in capable hands."}
            </Typography>
          </div>
        </div>
      </div>
      <div css={style.callInfo}>
        <WhiteLabelButton isFullWidth onClick={onClick}>
          Join interview
        </WhiteLabelButton>
        {!isVideoEnabled && !isDemo && (
          <>
            {phoneNumber ? (
              <div css={style.phoneNumber}>
                <Typography size="XXS" color={uiGray[70]}>
                  Join via phone:
                </Typography>
                <ButtonLink
                  href={`tel:${phoneNumber}`}
                  background="link"
                  customCss={style.button}
                >
                  {formatPhoneNumber(phoneNumber)}
                </ButtonLink>
              </div>
            ) : (
              <WhiteLabelButton
                isFullWidth
                background="link"
                onClick={async () => {
                  const res = await getInterviewPhoneNumber(interviewFlowId);
                  if (res?.phoneNumber) setPhoneNumber(res?.phoneNumber);
                }}
              >
                {"Join via phone"}
              </WhiteLabelButton>
            )}
          </>
        )}
      </div>
    </>
  );
};
